import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {
    CargoModel, CargoPaymentModel,
    CategoryTreeModel, PartiesModel,
    ProductCategoryModel,
    ProductModel,
    ProductTableModel,
    PublicProductModel
} from '../models/products.model';
import {ConnectorService} from './api/connector.service';

@Injectable({
    providedIn: 'root'
})
export class ProductionService extends ConnectorService {

    /**
     * Получение карточек по гуиду категории отваров
     * @param categoryGuid
     */
    gatProductCardsByCategory(categoryGuid: string): Observable<{ products: ProductTableModel[] }> {
        return this.get('instance/wms/category/' + categoryGuid + '/products');
    }

    /**
     * Создать карточку задачи
     * @param data
     */
    createProductCard(
        data: {
            title: string,
            categories_cuid: string,
        },
    ): Observable<{ product: ProductModel }> {
        return this.post('instance/wms/product_card', data);
    }

    /**
     * Редактирование приватной карточки
     * @param puid
     * @param data
     */
    editPrivateProductCard(
        puid: string,
        data: {
            title: string,
            description: string,
        },
    ): Observable<{ product: ProductModel }> {
        return this.put('instance/wms/product_card/update/' + puid + '/private', data);
    }

    /**
     * Получение дерева категорий
     * @param categoryGuid
     */
    getCategories(categoryGuid?: string): Observable<{ categories_tree: CategoryTreeModel[] }> {
        return this.get('instance/wms/tree_categories', categoryGuid ? {cuid: categoryGuid} : {});
    }

    /**
     * Создать категорию
     * @param data
     */
    createCategory(
        data: {
            title: string,
            parent_cuid: string,
        }
    ): Observable<{ category: ProductCategoryModel, puid: string }> {
        return this.post('instance/wms/category', data);
    }

    /**
     * Получение карточки товара
     * @param puid
     */
    getProductCard(puid: string): Observable<{ product: ProductModel }> {
        return this.get('instance/wms/product_card/' + puid);
    }

    /**
     * Получение публичной карточки товара
     * @param puid
     * @param publicPuid
     */
    getPublicProductCard(puid: string, publicPuid: string): Observable<{ product_public: PublicProductModel }> {
        return this.get('instance/wms/product_card/' + puid + '/public/' + publicPuid);
    }

    /**
     * Создание публичной карточки товара
     * @param puid
     */
    createPublicProductCard(puid: string): Observable<{ product: PublicProductModel }> {
        return this.post('instance/wms/product_card/' + puid + '/public');
    }

    /**
     * Получения списка публичных карточек товара
     * @param puid
     */
    getAllPublicCards(puid: string): Observable<{ product_public: PublicProductModel[] }> {
        return this.get('instance/wms/product_card/' + puid + '/public');
    }

    /**
     * Получить весь список партий товара
     * @param puid
     */
    getAllBatchesPublicCard(puid: string): Observable<{ parties: PartiesModel[] }> {
        return this.get(`instance/wms/product_card/${puid}/parties`);
    }

    /**
     * Добавление фотографий в карточку товара
     * @param puid
     * @param photo
     */
    addPhotoToProductCard(puid: string, photo: { photo: File }): Observable<{ message: string, product: ProductModel }> {
        const form = new FormData();
        for (const dataKey of Object.keys(photo)) {
            form.append(dataKey, photo[dataKey]);
        }
        return this.post('instance/wms/product_card/' + puid + '/photo', form, false);
    }

    /**
     * Получение изображения карточки
     * @param puid
     * @param photoUkey
     */
    getPhotoInProductCard(puid: string, photoUkey: string): Observable<Blob> {
        return this.getBodyFile('instance/wms/product_card/' + puid + '/image/' + photoUkey);
    }

    /**
     * Добавление коллекции в карточку товара
     * @param puid
     * @param collectionId
     */
    addCollectionToProductCard(puid: string, collectionId: number): Observable<{ message: string, product: ProductModel }> {
        return this.post('instance/wms/product_card/' + puid + '/collection', {collection_id: collectionId});
    }

    /**
     * Удалить коллекцию из карточки товара
     * @param collectionId
     * @param puid
     */
    removeCollectionFromProductCard(collectionId: number, puid: string): Observable<{ message: string, product: ProductModel }> {
        return this.delete('instance/wms/product_card/' + puid + '/collection/' + collectionId);
    }

    /**
     * Создать груз
     */
    createCargo(): Observable<{ cargo: CargoModel }> {
        return this.post('instance/wms/cargo');
    }

    /**
     * Получение всех грузов
     */
    getAllCargos(): Observable<{ cargos: CargoModel[] }> {
        return this.get('instance/wms/cargos');
    }

    /**
     * Добавить партию в груз
     * @param cargoKey
     * @param data
     */
    addBatchToCargo(
        cargoKey: string,
        data: {
            product_public_puid: string,
            counter_type: string,
            count: number,
            price: number,
        }
    ): Observable<{ cargo: CargoModel }> {
        return this.put('instance/wms/cargo/' + cargoKey, data);
    }

    /**
     * Получение всех активных транзакций
     */
    getAllActiveTransaction(): Observable<{ transactions: CargoPaymentModel[] }> {
        return this.get('instance/wms/transaction/list');
    }

    /**
     * Создать транзакцию
     */
    createTransaction(): Observable<{ transaction: CargoPaymentModel }> {
        return this.post('instance/wms/transaction');
    }

    getLastHundredProduction(type: 'SERVICE' | 'PRODUCT'): Observable<any> {
        return this.get('instance/wms/product_list', {card_type: type});
    }

    /**
     * TODO тут под вопросом, будет ли метод, если будет, то расписать модели
     * Удаление транзакции
     * @param transLey
     */
    deleteTransaction(transLey: string): Observable<any> {
        return this.delete('instance/wms/transaction/' + transLey);
    }

    /**
     * TODO расписать модели
     * Получение транзакции по ключу
     * @param transKey
     */
    getTransactionByKey(transKey: string): Observable<any> {
        return this.get('instance/wms/transaction/' + transKey);
    }

    /**
     * Добавление грузов в открытую транзакцию. Если открытой транзакции нет, то создает новую.
     * @param cargoKeys
     */
    addCargosToTransaction(cargoKeys: string): Observable<{ transactions: CargoPaymentModel[] }> {
        return this.post('instance/wms/transaction/add_cargo', {cargo_keys: cargoKeys});
    }

    /**
     * TODO расписать модели
     * Удаление груза из транзакции
     * @param transKey
     * @param cargoKey
     */
    deleteCargoFromTransaction(transKey: string, cargoKey: string): Observable<any> {
        return this.delete('instance/wms/transaction/' + transKey + '/' + cargoKey);
    }

    /**
     * TODO расписать модели
     * Отправка транзакции на упаковку
     * @param transKey
     */
    packagingTransaction(transKey: string): Observable<any> {
        return this.post('instance/wms/transaction/' + transKey + '/packaging');
    }

}
